import { registerNsbEventResetter } from './nsbEventReset';

let applied = true;
let paintTick = 0;

let fetchesInFlight = 0;
let applierAvailable = false;

let nsbEnabled = false;

let awaitingBuyer = false;

const subs = new Set<() => void>();

const notify = (): void => { subs.forEach(fn => fn()); };

/**
 * Whether the NSB cell renderers may be installed in the grid.
 *
 * Default true: opening an existing event behaves exactly as it always has. Only an import withholds
 * them, because that is the case the cost lands in — the grid cannot draw a row until these renderers
 * have run, which is why a 500-item upload took ~11s with NSB on against ~2s with it off. The match
 * sweep is not the problem: it writes into the stores these renderers read, and runs whether or not
 * they are mounted. So a freshly imported grid paints plain, the sweep proceeds untouched, and the
 * buyer takes the Smart Baseline cells when it reports complete.
 */
export const isNsbApplied = (): boolean => applied;

/**
 * The buyer's answer: install the cells. Idempotent - the toast's button can be pressed twice before its
 * card tears down.
 */
export const applyNsbCells = (): void => {
  if (!nsbEnabled) return;
  awaitingBuyer = false;
  if (applied) return;
  applied = true;
  notify();
};

/**
 * Called at the import boundaries and by a manual Refresh: withhold the cells and offer them to the buyer,
 * which leaves the offer pending (`awaitingBuyer`) until the buyer answers it through applyNsbCells.
 * An import is the one moment the buyer is waiting on a table they cannot use yet, so it is the one moment
 * worth withholding the cells for.
 *
 * Marks the offer as pending even when the cells are already withheld - an arrival withholds them too, and
 * an upload straight after arriving must still wait for the buyer rather than ride the arrival's release.
 */
export const withholdNsbCells = (): void => {
  if (!nsbEnabled || !applierAvailable) return;
  awaitingBuyer = true;
  if (!applied) return;
  applied = false;
  paintTick = 0;
  notify();
};

/**
 * Event load: an event that has already been saved with baselines has nothing to offer, so its cells go
 * straight in - unless an offer is pending.
 *
 * ⛔ An import saves and reloads on its own, before the buyer has answered. The rows it saved carry
 * sources (the backend stamps ICO on every unpriced row while ICO is on), so this load used to read the
 * upload as "already saved" and install the cells. The Apply offer never appeared and the prices were
 * applied without asking - only on uploads that happened to save a source, which is why it was
 * inconsistent. A pending offer is the buyer's to accept; no load may answer it for them.
 */
export const applyNsbCellsForSavedEvent = (): void => {
  if (awaitingBuyer) return;
  applyNsbCells();
};

/**
 * Not coalesced, unlike the neighbouring NSB stores. Those are written per row during hydration; this
 * flips once, from a click.
 */
export const subscribeNsbApply = (fn: () => void): (() => void) => {
  subs.add(fn);
  return () => { subs.delete(fn); };
};

/**
 * Bumped by every NSB cell that renders. Read by polling, deliberately: there is no subscription here
 * because a thousand cells each notifying would be a thousand re-renders of whoever listened.
 *
 * Exists because none of the obvious signals say when the prices are on screen. Setting `applied` is
 * instant. The grid's own render callback fires almost immediately — the grid is quick, it is the cells
 * that are slow, filling in one by one over seconds. So the cells are the only thing that knows, and a
 * caller watching this stop climbing is watching the real event.
 */
/**
 * Register whether anything is currently able to put the cells back.
 *
 * ⛔ The invariant this exists for: never withhold what nothing can restore. Withholding happens in the
 * import reducer, which is pure and cannot read a feature flag, while the only thing that can apply the
 * cells is the match toast — and the toast renders behind its own flag. With that flag off and Smart
 * Baseline on, an import stripped the cells and left the buyer no way to ask for them back for the rest
 * of the session.
 *
 * So the toast declares itself here while it is mounted, and withholdNsbCells does nothing unless it has.
 * The fallback is simply the old behaviour: the cells stay installed and the import is slower to paint.
 */
export const setNsbApplierAvailable = (available: boolean): void => { applierAvailable = available; };

/**
 * Publishes the NSB layer's gate — `tlcNewSmartBaseline` && an RFP event — from LineItemsProvider.
 *
 * EVERY state change in this store is gated on it, so a customer without the flag cannot have their
 * cells withheld or re-applied by any caller, including the pure import reducers in actions.ts that
 * have no way to read a LaunchDarkly flag themselves. One gate named after the flag, rather than a
 * condition repeated at each call site and forgotten at the next one.
 *
 * Turning it off releases any withholding in progress, so the cells cannot be left hidden by a flag
 * change mid-session. With NSB off the store is frozen at "cells installed" — the pre-branch behaviour.
 */
export const setNsbEnabled = (enabled: boolean): void => {
  nsbEnabled = enabled;
  if (!enabled) awaitingBuyer = false;
  if (!enabled && !applied) {
    applied = true;
    paintTick = 0;
    fetchesInFlight = 0;
    notify();
  }
};

/**
 * Count a cell's price fetch as started / finished. `fetchesInFlight` is how many NSB cells are waiting
 * on a price right now.
 *
 * ⭐ This is what tells the toast that applying is DONE, and it replaced a 1.5-second "no cells have
 * painted lately" window. That window was not a scalable signal: it assumed a pause in painting meant
 * the work had ended, when a pause is exactly what a slow fetch looks like. Measured on a build without
 * the prices-only tier, cells pulled the full payload, the gaps between paints exceeded the window, and
 * the toast announced "applied" while the grid was still visibly filling.
 *
 * Counting the work itself holds whatever the fetch costs and however many rows there are — which is the
 * property the timer never had. Two integer operations per cell; nothing is fetched or re-rendered on
 * account of it.
 */
export const noteNsbFetchStarted = (): void => { fetchesInFlight += 1; };

export const noteNsbFetchFinished = (): void => { fetchesInFlight = Math.max(0, fetchesInFlight - 1); };

export const getNsbFetchesInFlight = (): number => fetchesInFlight;

export const getNsbPaintTick = (): number => paintTick;

export const noteNsbCellPainted = (): void => { paintTick += 1; };

/**
 * Back to WITHHELD wherever there is an applier to restore them - so every arrival at an event starts
 * from scratch: the grid paints plain, the buyer is offered the match, and the cells go in when they ask.
 *
 * This used to reset to applied, on the reasoning that "a re-entry is a plain load and a plain load has
 * always shown the NSB cells". ⛔ That is what made a returning visit apply the cells behind the buyer's
 * back: nothing was left to apply, so the offer never appeared and the prices simply arrived. Carrying
 * ANY state across a visit has bitten this feature every time - see the toast's own notes.
 *
 * Without an applier (Smart Baseline off) there is nothing that could put the cells back, so withholding
 * them would strand the grid permanently. Those customers keep the old behaviour.
 */
export const resetNsbApplied = (): void => {
  applied = !applierAvailable;
  awaitingBuyer = false;
  paintTick = 0;
  fetchesInFlight = 0;
  notify();
};

registerNsbEventResetter(resetNsbApplied);
